/* eslint-disable no-param-reassign */
import axios, {
  AxiosRequestConfig,
  AxiosHeaders,
  AxiosResponse,
  AxiosError,
} from "axios";

/* eslint-disable import/extensions -- @/common paths resolved via tsconfig */
import { getCurrentReceivedUrl } from "@/common/currentPageUrl";
import { getHostnameFromUrl, getPublicSubdomainPart } from "@/common/domain";
import { webConfig } from "@/v2/utils/config";
import { AUTH_KEY } from "@/v2/utils/constants";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosHeaders;
  _retry?: boolean;
}

export interface AxiosResponseData<T> {
  data: T;
  status: number;
  statusText: string;
  headers: AxiosHeaders;
  config: CustomAxiosRequestConfig;
}

const { VITE_BACKEND_API_URL } = webConfig;
const exceptUrls = [
  "/chat/register",
  "/chat/login",
  "/chat/auth/line/callback",
  "web-agent-data",
  "reset-password-chatbot",
  "change-password-chatbot",
  "line/login",
];

// Ensure VITE_BACKEND_API_URL is defined
if (!VITE_BACKEND_API_URL) {
  throw new Error(
    "VITE_BACKEND_API_URL is not defined in the environment variables.",
  );
}

const axiosInstance = axios.create({
  baseURL: VITE_BACKEND_API_URL,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Middleware: Request Interceptor
axiosInstance.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    const authData = JSON.parse(localStorage.getItem(AUTH_KEY) || "{}");
    // Authorization
    if (authData) {
      config.headers.Authorization = `Bearer ${authData.access_token}`;
    }
    // Public subdomain: set public-page header when current page is from a public subdomain.
    // Use document.referrer as fallback (e.g. when SET_URL not yet received or lost on slow load / mobile).
    const receivedUrl = getCurrentReceivedUrl() || document.referrer || ""; 
    if (receivedUrl) {
      const hostname = getHostnameFromUrl(receivedUrl);
      const publicPage = hostname ? getPublicSubdomainPart(hostname) : null;
      if (publicPage) {
        config.headers.set("public-page", publicPage);
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

function redirectToLogin() {
  localStorage.clear();
  window.dispatchEvent(new CustomEvent("auth:logout"));
}

// Middleware: Response Interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    const isExceptUrl = exceptUrls.some((url) =>
      originalRequest.url?.includes(url),
    );
    const is401 = error.response?.status === 401;

    if (!isExceptUrl && is401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const auth = JSON.parse(localStorage.getItem(AUTH_KEY) || "{}");
        if (!auth?.refresh_token) throw new Error("No refresh token found");

        const response = await axios.post(
          `${VITE_BACKEND_API_URL}/chat/access-token`,
          { refresh_token: auth.refresh_token },
        );

        const { access_token } = response.data;
        localStorage.setItem(
          AUTH_KEY,
          JSON.stringify({ ...auth, access_token }),
        );

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return await axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
